import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { callEndpoint, setStaffId, setUsername } from 'redux/global';
import { setHotelData } from 'pages/redux';
import { IResponse } from 'types';
import {
  createInitialDescriptionBlocks,
  ReportDescriptionBlock,
} from './reportDescriptionBlocks';
import {
  createDefaultReportingComposer,
  ReportingComposerState,
} from './reportingComposerState';
import {
  clearReportingComposerStorage,
  loadReportingComposer,
  reportingComposerUserKey,
  saveReportingComposer,
} from './reportingComposerStorage';
import {
  loadReportingNavItem,
  REPORTING_NAV_START,
  ReportingNavItemId,
  saveReportingNavItem,
  sanitizeReportingNavItem,
} from './reportingNavStorage';
import { ReportContextUser, sanitizeReportContextUsers } from './reportContextUsers';
import { clearPendingReportingComposerRestore } from './reportingComposerRestore';

export type {
  PreparedReportDraft,
  ReportingComposerState,
} from './reportingComposerState';
export { createDefaultReportingComposer } from './reportingComposerState';

export type ManagerReportKind = 'report' | 'response';

export type ManagerReport = {
  id: string,
  toEmail: string,
  toName: string,
  fromEmail: string,
  fromName: string,
  description: string,
  /** Mandatory subject when present (newer reports). */
  subject?: string,
  preview: string,
  pdfGcsPath: string,
  createdAt: string,
  reportKind?: ManagerReportKind,
  parentId?: string | null,
  data?: Record<string, unknown>,
  viewUrl?: string,
  /** GCS signed URL with attachment disposition — Save-as from the viewer. */
  downloadUrl?: string,
  /** True when view/download PDF is the whole thread (report + responses). */
  isThreadSetPdf?: boolean,
  threadSetLabel?: string,
  threadItemCount?: number,
  /** This staff marked it filed or dismissed — removed 7 days later. */
  reviewedAt?: string | null,
  /** File records user alerts; dismiss does not. Legacy reviewed rows count as filed. */
  resolution?: 'filed' | 'dismissed' | null,
  /** Set when a higher-permission manager closed this report from Audits. */
  closedAt?: string | null,
  closedByName?: string | null,
  /** True when this is a response the signed-in staff sent. */
  isOutgoing?: boolean,
};

export type PlainAuditRowPayload = {
  type: string,
  message: string,
  date?: string,
  value?: string,
  units?: string,
  /** Frozen /audits Value-header mode for this row's set. */
  valueMode?: 'value' | 'units',
  /** Frozen /audits Audit-header attribute for this row's set. */
  typeAttributeMode?: 'department' | 'inventory_group' | 'sku',
  /** Temporary report row number (1-based). */
  tmpId?: number,
  /** Real activity_logs id — for opening in Audits. */
  sourceId?: string,
  /** User-marked reference under the description. */
  highlighted?: boolean,
  /** Actual log type / SKU when the Audit column is a rollup. */
  auditType?: string,
};

export type AuditGroupPayload = {
  label?: string,
  rows: PlainAuditRowPayload[],
};

/** Snapshot of Audits filters so "Show logs" can reopen the same set. */
export type AuditsJumpPayload = {
  search: string,
  searchNegate?: boolean,
  searchMatchMode?: 'any' | 'room' | 'staff' | 'customer' | 'department' | 'ota',
  searchExactMatch?: boolean,
  hideArchives?: boolean,
  checked: string[],
  startDate: string,
  endDate: string,
  valueMode?: 'value' | 'units',
  /**
   * Author identity for Show logs on the other party's item.
   * Own items omit this and stay as the signed-in staff.
   */
  viewAsPermission?: number,
  viewAsStaffId?: string,
  viewAsUsername?: string,
  viewAsLabel?: string,
  /** Audits Combine SKU at report send / Show logs time. */
  combineSku?: boolean,
  /** /audits Audit-header attribute at report send / Show logs time. */
  typeAttributeMode?: 'department' | 'inventory_group' | 'sku',
};

/** Ordered description body for PDF / preview (text interleaved with log chips). */
export type DescriptionBlockPayload = {
  kind: 'text' | 'log',
  text?: string,
  sourceId?: string,
  tmpId?: number,
  message?: string,
  type?: string,
  date?: string,
  value?: string,
  units?: string,
  combinedSourceIds?: string[],
  valueMode?: 'value' | 'units',
};

type ReportingState = {
  reports: ManagerReport[] | null,
  isLoading: boolean,
  composer: ReportingComposerState,
  /** staffId/username + hotel the in-memory composer belongs to — swap on login. */
  composerOwnerKey: string,
  /** Last left-pane item — restored on refresh / leaving the page. */
  navItem: ReportingNavItemId,
};

const reportingSessionOwnerKey = (): string => {
  const userKey = reportingComposerUserKey();
  if (!userKey) return '';
  const hotelId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('hotel_data_id'))
    || 'default';
  return `${hotelId}:${userKey}`;
};

const ownerUserKey = (sessionOwnerKey: string): string => {
  const i = sessionOwnerKey.indexOf(':');
  return i < 0 ? sessionOwnerKey : sessionOwnerKey.slice(i + 1);
};

/** Load this staff's draft, or empty when signed out. Does not wipe another user's localStorage. */
const applyComposerForCurrentSession = (state: ReportingState): void => {
  const ownerKey = reportingSessionOwnerKey();
  if (ownerKey === state.composerOwnerKey) return;
  const userChanged = ownerUserKey(ownerKey) !== ownerUserKey(state.composerOwnerKey);
  state.composerOwnerKey = ownerKey;
  // Inbox is per recipient — drop it when staff changes, not when hotel id catches up.
  if (userChanged) state.reports = null;
  if (!ownerKey) {
    state.composer = createDefaultReportingComposer();
    state.navItem = REPORTING_NAV_START;
    return;
  }
  state.composer = loadReportingComposer();
  state.navItem = loadReportingNavItem();
};

const initialState: ReportingState = {
  reports: null,
  isLoading: false,
  // Restore last composer on full page refresh (this staff only).
  composer: loadReportingComposer(),
  composerOwnerKey: reportingSessionOwnerKey(),
  navItem: loadReportingNavItem(),
};

const reportingSlice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {
    setManagerReports: (state, action: PayloadAction<ManagerReport[] | null>) => {
      state.reports = action.payload;
    },
    setManagerReportsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    removeManagerReport: (state, action: PayloadAction<string>) => {
      if (!state.reports) return;
      state.reports = state.reports.filter((r) => r.id !== action.payload);
      if (state.navItem === action.payload) {
        state.navItem = REPORTING_NAV_START;
        saveReportingNavItem(REPORTING_NAV_START);
      }
    },
    patchManagerReport: (state, action: PayloadAction<ManagerReport>) => {
      if (!state.reports) {
        state.reports = [action.payload];
        return;
      }
      const next = action.payload;
      const exists = state.reports.some((r) => r.id === next.id);
      state.reports = exists
        ? state.reports.map((r) => (r.id === next.id ? { ...r, ...next } : r))
        : [next, ...state.reports];
    },
    setReportingNavItem: (state, action: PayloadAction<ReportingNavItemId>) => {
      const next = sanitizeReportingNavItem(action.payload);
      state.navItem = next;
      saveReportingNavItem(next);
    },
    /** Patch composer fields (recipient, description, draft, …). */
    setReportingComposer: (state, action: PayloadAction<Partial<ReportingComposerState>>) => {
      const next = {
        ...state.composer,
        ...action.payload,
      };
      state.composer = {
        ...next,
        descBlocks: Array.isArray(next.descBlocks) && next.descBlocks.length
          ? next.descBlocks
          : createInitialDescriptionBlocks(),
      };
      saveReportingComposer(state.composer);
    },
    resetReportingComposer: (state) => {
      state.composer = createDefaultReportingComposer();
      clearReportingComposerStorage();
      // New draft / send / reply — do not keep the previous form's scroll.
      clearPendingReportingComposerRestore();
    },
    /** Re-read this staff's localStorage draft (login / hotel change). */
    hydrateReportingComposer: (state) => {
      applyComposerForCurrentSession(state);
      if (state.composerOwnerKey) {
        state.navItem = loadReportingNavItem();
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setStaffId, (state) => {
      applyComposerForCurrentSession(state);
    });
    builder.addCase(setUsername, (state) => {
      applyComposerForCurrentSession(state);
    });
    builder.addCase(setHotelData, (state) => {
      applyComposerForCurrentSession(state);
    });
  },
});

export const {
  setManagerReports,
  setManagerReportsLoading,
  removeManagerReport,
  patchManagerReport,
  setReportingNavItem,
  setReportingComposer,
  resetReportingComposer,
  hydrateReportingComposer,
} = reportingSlice.actions;

export const selectManagerReports = (state: RootState) => state.reporting.reports;
export const selectManagerReportsLoading = (state: RootState) => state.reporting.isLoading;
export const selectReportingNavItem = (state: RootState) => (
  sanitizeReportingNavItem(state.reporting.navItem)
);
export const selectReportingComposer = (state: RootState) => {
  const c = state.reporting.composer;
  return {
    ...c,
    subject: typeof c.subject === 'string' ? c.subject : '',
    descBlocks: Array.isArray(c.descBlocks) && c.descBlocks.length
      ? c.descBlocks
      : createInitialDescriptionBlocks(),
    auditSets: Array.isArray(c.auditSets) ? c.auditSets : [],
    chartsMetricMode: 'balances' as const,
    replyToReportId: typeof c.replyToReportId === 'string' && c.replyToReportId
      ? c.replyToReportId
      : null,
    replyToFromName: typeof c.replyToFromName === 'string' ? c.replyToFromName : '',
    composerPhase: c.composerPhase === 'body' ? 'body' : 'details',
    contextUsers: sanitizeReportContextUsers(c.contextUsers),
    alertLevelSummary: typeof c.alertLevelSummary === 'string' ? c.alertLevelSummary : '',
  };
};

/**
 * Functional update for description blocks — reads latest composer so navigate-back
 * edits never stomp each other.
 */
export const updateReportingDescBlocks = (
  updater: (prev: ReportDescriptionBlock[]) => ReportDescriptionBlock[],
) => (dispatch: Function, getState: () => RootState) => {
  const prev = selectReportingComposer(getState()).descBlocks;
  dispatch(setReportingComposer({ descBlocks: updater(prev) }));
};

export const isReportResponse = (report?: ManagerReport | null): boolean => (
  // Follow-up reports on a thread also have parentId — only kind=response is a reply.
  report?.reportKind === 'response'
);

/** Original report this staff created — not a reply they sent. */
export const isOutgoingCreatedReport = (report?: ManagerReport | null): boolean => (
  Boolean(report?.isOutgoing) && !isReportResponse(report)
);

export const isManagerReportActive = (report?: ManagerReport | null): boolean => (
  Boolean(report) && !report?.reviewedAt
);

export const isManagerReportDismissed = (report?: ManagerReport | null): boolean => (
  Boolean(report?.reviewedAt) && report?.resolution === 'dismissed'
);

export const isManagerReportFiled = (report?: ManagerReport | null): boolean => (
  Boolean(report?.reviewedAt) && report?.resolution !== 'dismissed'
);

export const fetchManagerReports = () => async (dispatch: Function): Promise<IResponse> => {
  dispatch(setManagerReportsLoading(true));
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'managerreports',
      method: 'GET',
    }),
  );
  dispatch(setManagerReportsLoading(false));
  if (res.status === 'success') {
    dispatch(setManagerReports(Array.isArray(res.data) ? res.data : []));
  }
  return res;
};

/** Chart PNG for PDF embedding (from Chart.js offscreen render). */
export type ReportChartImagePayload = {
  id: string,
  title: string,
  dataUrl: string,
};

/** Create a report (PDF + app alert inbox). */
export const prepareManagerReport = (body: {
  toEmail: string,
  subject: string,
  description: string,
  auditGroups?: AuditGroupPayload[],
  auditsJump?: AuditsJumpPayload | null,
  descriptionBlocks?: DescriptionBlockPayload[],
  /** Per set (+ live): text then charts for that serial — PDF / receiver order. */
  reportSections?: {
    serial: number,
    descriptionBlocks: DescriptionBlockPayload[],
    /** Reporting-only type rollup for this set's description preview/PDF. */
    combineLogTypes?: boolean,
    /** Frozen activity_logs ids for this set — receiver Show all logs. */
    sourceIds?: string[],
    /** Frozen /audits Value-header for this set's clickable logs. */
    valueMode?: 'value' | 'units',
    /** Frozen /audits Audit-header attribute for this set's clickable logs. */
    typeAttributeMode?: 'department' | 'inventory_group' | 'sku',
  }[],
  /** When false, PDF + receiver skip charts. */
  showCharts?: boolean,
  /** counts | balances — stored for receiver rebuild. */
  chartsMetricMode?: 'counts' | 'balances',
  /** Serialized metrics for receiver preview rebuild (first set / legacy). */
  metrics?: { logCount: number, charts: unknown[] } | null,
  /** Per audit-set metrics for stacked receiver preview. */
  metricsBySet?: {
    serial: number,
    /** Dark set label in the chart header (e.g. Audits 1). */
    chartsLabel?: string,
    logCount: number,
    valueLogCount?: number,
    statusNote?: string | null,
    charts: unknown[],
  }[],
  chartImages?: ReportChartImagePayload[],
  /** Set when this send is a reply to an inbox report. */
  parentId?: string | null,
  /** Up to 5 staff named in this report, each Favorable / Neutral / Unfavorable. */
  contextUsers?: ReportContextUser[],
  /** Required when any in-context person is Favorable or Unfavorable. */
  alertLevelSummary?: string,
}) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'sendmanagerreport',
      method: 'POST',
      body,
    }),
  );
  return res;
};

/** Build a composer preview PDF without sending or saving a report. */
export const previewManagerReportPdf = (body: {
  toEmail?: string,
  subject?: string,
  description: string,
  auditGroups?: AuditGroupPayload[],
  auditsJump?: AuditsJumpPayload | null,
  descriptionBlocks?: DescriptionBlockPayload[],
  reportSections?: {
    serial: number,
    descriptionBlocks: DescriptionBlockPayload[],
    combineLogTypes?: boolean,
    sourceIds?: string[],
    valueMode?: 'value' | 'units',
    typeAttributeMode?: 'department' | 'inventory_group' | 'sku',
  }[],
  showCharts?: boolean,
  metricsBySet?: {
    serial: number,
    chartsLabel?: string,
    logCount: number,
    valueLogCount?: number,
    statusNote?: string | null,
    charts: unknown[],
  }[],
  chartImages?: ReportChartImagePayload[],
  contextUsers?: ReportContextUser[],
  alertLevelSummary?: string,
}) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'previewmanagerreport',
      method: 'POST',
      body,
    }),
  );
  return res;
};

/** @deprecated use prepareManagerReport — kept name for older imports */
export const sendManagerReport = prepareManagerReport;

export const replyManagerReport = (body: {
  parentId: string,
  description: string,
}) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'replymanagerreport',
      method: 'POST',
      body,
    }),
  );
  return res;
};

export const fetchManagerReportView = (
  id: string,
  threadIds?: string[],
) => async (dispatch: Function): Promise<IResponse> => {
  const ids = (threadIds || [])
    .map((itemId) => String(itemId || '').trim())
    .filter(Boolean);
  const qs = ids.length
    ? `?threadIds=${ids.map((itemId) => encodeURIComponent(itemId)).join(',')}`
    : '';
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}/view${qs}`,
      method: 'GET',
    }),
  );
  return res;
};

export const clearManagerReport = (id: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}`,
      method: 'DELETE',
    }),
  );
  if (res.status === 'success') {
    dispatch(removeManagerReport(id));
  }
  return res;
};

/** File or dismiss an inbox / sent item — dropped 7 days later. */
export const markManagerReportReviewed = (
  id: string,
  resolution: 'filed' | 'dismissed' = 'filed',
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}/review`,
      method: 'POST',
      body: { resolution },
    }),
  );
  if (res.status === 'success' && res.data && typeof res.data === 'object') {
    dispatch(patchManagerReport(res.data as ManagerReport));
  }
  return res;
};

/** Bring a filed or dismissed item back to Active. Filed user alerts are removed. */
export const reopenManagerReport = (id: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}/reopen`,
      method: 'POST',
    }),
  );
  if (res.status === 'success' && res.data && typeof res.data === 'object') {
    dispatch(patchManagerReport(res.data as ManagerReport));
  }
  return res;
};

export type ClosedReportAlertPreview = {
  logId: string,
  username: string,
  standing: 'good' | 'neutral' | 'bad',
  kind: 'staff' | 'guest',
};

export type CloseReviewedReportPreview = {
  report: ManagerReport,
  alertLevelSummary: string,
  thisAlert: ClosedReportAlertPreview | null,
  otherAlerts: ClosedReportAlertPreview[],
  otherAlertCount: number,
};

export const fetchCloseReviewedReportPreview = (
  opts: { auditId?: string, reportId?: string },
) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'managerreport/closepreview',
      method: 'POST',
      body: opts,
    }),
  );
  return res;
};

export const peekManagerReport = (id: string) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}/peek`,
      method: 'GET',
    }),
  );
  return res;
};

export const closeReviewedManagerReport = (
  id: string,
  auditId?: string,
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `managerreport/${id}/close`,
      method: 'POST',
      body: auditId ? { auditId } : {},
    }),
  );
  if (res.status === 'success') {
    dispatch(removeManagerReport(id));
  }
  return res;
};

export default reportingSlice.reducer;
